import React, { useCallback, useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import {
  getCoreRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable
} from '@tanstack/react-table'
import { Input, Modal, Table as TableCortex } from '@tecsinapse/cortex-react'
import { IoClose } from 'react-icons/io5'
import {
  Produto,
  type UsuariosTipoAcessoByTipoQuery,
  type UsuariosTipoAcessoByTipoQueryVariables
} from '@/core/types/graphql'
import { rowsPerPageOptions } from '@/core/utils/rows-per-page'
import type { OptionSelect } from '@/core/utils/types'
import { EmptyList } from '@/core/components/empty-list'
import { Table } from '@/core/components/index'
import { USUARIOS_TIPOS_ACESSOS_BY_TIPO } from '../../data/usuarios-tipo-acesso-by-tipo'
import { columnsPermissao, UsuarioPermissaoItem } from '../columns-permissao'
import { CustomProps, ModalConfirmacao } from './modal-confirmacao'
import { useWatch } from 'react-hook-form'
import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'

const mapAndFilterData = (
  usuarios: UsuariosTipoAcessoByTipoQuery['usuariosByTipoUsuario'],
  produtos: Produto[]
): UsuarioPermissaoItem[] => {
  const produtosIds = new Set(produtos.map(p => p.id))

  return (usuarios ?? [])
    .map(usuario => {
      const usuarioClonado = { ...usuario, tiposAcessoByProduto: new Map() }

      for (const tipoAcesso of usuario?.tiposAcesso ?? []) {
        const idProduto = tipoAcesso?.produto.id

        if (produtosIds.has(String(idProduto))) {
          if (!usuarioClonado.tiposAcessoByProduto.has(idProduto)) {
            usuarioClonado.tiposAcessoByProduto.set(idProduto, {
              produto: tipoAcesso?.produto,
              tiposAcesso: new Set()
            })
          }

          usuarioClonado.tiposAcessoByProduto
            .get(idProduto)
            .tiposAcesso.add(tipoAcesso)
        }
      }

      return usuarioClonado
    })
    .filter(usuario =>
      Array.from(usuario.tiposAcessoByProduto.keys()).some(idProduto =>
        produtosIds.has(idProduto)
      )
    )
    .map(usuario => ({
      ...usuario,
      tiposAcessoByProduto: Object.fromEntries(
        Array.from(usuario.tiposAcessoByProduto.entries()).map(
          ([id, value]) => [
            id,
            { ...value, tiposAcesso: Array.from(value.tiposAcesso) }
          ]
        )
      )
    })) as UsuarioPermissaoItem[]
}

export interface UsuarioClonarPermissaoProps {
  customProps: CustomProps
  onClose: () => void
  onOpenTable: () => void
}

const UsuarioClonarPermissaoModal = (props: UsuarioClonarPermissaoProps) => {
  const { customProps, onClose, onOpenTable } = props
  const values = useWatch()
  const { tipo: tipoUsuario } = values
  const { produtos } = customProps

  const [isConfirmacaoOpen, setIsConfirmacaoOpen] = useState(false)

  const handleOpenConfirmacao = useCallback(
    () => setIsConfirmacaoOpen(true),
    []
  )
  const handleCloseConfirmacao = useCallback(
    () => setIsConfirmacaoOpen(false),
    []
  )
  const [selectedRow, setSelectedRow] = useState<UsuarioPermissaoItem>()
  const [inputSearch, setInputSearch] = useState('')

  const { data, loading } = useQuery<
    UsuariosTipoAcessoByTipoQuery,
    UsuariosTipoAcessoByTipoQueryVariables
  >(USUARIOS_TIPOS_ACESSOS_BY_TIPO, {
    variables: {
      busca: inputSearch,
      tipoUsuario: tipoUsuario.value,
      dealerIds:
        values.idsDealers?.length > 0
          ? values.idsDealers.map((i: OptionSelect) => i.value)
          : []
    },
    fetchPolicy: 'network-only'
  })
  const { usuariosByTipoUsuario = [] } = data ?? {}
  const hasUsuario = useMemo(
    () => (usuariosByTipoUsuario ?? []).length > 0,
    [usuariosByTipoUsuario]
  )

  const dataTest = useMemo(
    () => mapAndFilterData(usuariosByTipoUsuario, produtos),
    [usuariosByTipoUsuario, produtos]
  )

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: rowsPerPageOptions[0]
  })

  const table = useReactTable({
    data: dataTest ?? [],
    columns: columnsPermissao({
      handleOpenConfirmacao,
      setSelectedRow,
      handleCloseClonar: onClose
    }),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    rowCount: dataTest?.length ?? 0,
    state: {
      pagination
    },
    onPaginationChange: setPagination
  })

  return (
    <>
      <Modal
        open={customProps.open}
        onClose={onClose}
        className={'flex flex-col max-h-[80vh] overflow-y-scroll w-[70vw]'}
      >
        <div className={'self-end mb-deca'}>
          <IoClose
            className={'text-secondary-xdark  cursor-pointer'}
            onClick={onClose}
          />
        </div>
        <div className={'mb-deca w-1/2'}>
          <Input.Search
            placeholder={t`Busque por nome ou email`}
            label={t`Busque por nome ou email`}
            onChange={e => setInputSearch?.(e.target.value)}
            defaultValue={inputSearch}
            disabled={loading}
          />
        </div>
        {dataTest.length > 0 || loading ? (
          <>
            <div className={'mb-deca'}>
              <p className={'font-bold'}>
                <Trans>
                  Selecione o usuário desejado para clonar suas permissões:
                </Trans>
              </p>
            </div>
            <TableCortex className={'w-full'}>
              <Table.HeaderColumns table={table} />
              <Table.Body table={table} loading={loading} />
              <Table.Footer
                table={table}
                rowsPerPageOptions={rowsPerPageOptions}
                loading={loading}
                countList={dataTest?.length}
              />
            </TableCortex>
          </>
        ) : (
          <EmptyList
            title={
              hasUsuario
                ? t`Usuário não permitido.`
                : t`Usuário não encontrado.`
            }
            subTitle={
              hasUsuario
                ? t`O usuário pesquisado não possui acesso a esse produto.`
                : t`Não encontramos nenhum usuário correspondente a sua pesquisa.`
            }
          />
        )}
      </Modal>
      <ModalConfirmacao
        {...{
          customProps,
          onOpenTable,
          isConfirmacaoOpen,
          handleCloseConfirmacao,
          selectedRow
        }}
      />
    </>
  )
}

export default UsuarioClonarPermissaoModal
